import { useCallback } from "react";

import type {
  FlightSearchPagedResultData,
  FlightSearchParams,
} from "@/features/flights/hooks/flightSearch/types";
import type { IFlightFilters } from "@/types/interfaces";

import { useFlightSearch } from "./useFlightsSearch";
import { isFlightOffer } from "./utils";

export const useReturnFlightSearch = (
  params: FlightSearchParams,
  filters: IFlightFilters | null,
  selectedOfferIndex: string | null,
  selectedDepartureFlightId: string | null,
) => {
  const select = useCallback(
    (response: FlightSearchPagedResultData) => {
      if (!selectedDepartureFlightId)
        throw Error(
          "Selecting return flight without selected departure flight is not allowed",
        );

      const offers = response.pages.flatMap((page) => page.offers);
      const filteredOffers = offers
        .filter(
          (offer) => isFlightOffer(offer) && offer.index === selectedOfferIndex,
        )
        .map((offer) =>
          isFlightOffer(offer)
            ? {
                ...offer,
                flightsOverview: offer.flightsOverview.filter((fo) =>
                  fo.flightReferences.includes(selectedDepartureFlightId),
                ),
              }
            : offer,
        );

      return filteredOffers;
    },
    [selectedDepartureFlightId, selectedOfferIndex],
  );

  const query = useFlightSearch(params, filters, { select });

  const hasReachedEnd = !query.hasNextPage;
  const isEmpty = !query.result || query.result.length === 0;

  return {
    ...query,
    hasReachedEnd,
    isEmpty,
  };
};
